// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import * as React from 'react';
import {
  Paintbrush,
  Type,
  Square,
  Circle,
  Minus,
  RotateCw,
  Undo2,
  Save,
  MousePointer2,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { fabric } from 'fabric';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useToast } from '../ui/toast';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import * as mutations from '../../../graphql/mutations';

interface FabricCanvasWithElements extends fabric.Canvas {
  upperCanvasEl: HTMLCanvasElement;
  lowerCanvasEl: HTMLCanvasElement;
}

export interface PhotoAnnotatorProps {
  open: boolean;
  onClose: () => void;
  photo: {
    imageName: string;
    tags: string[];
    description: string;
    fileId?: string;
    type?: string;
  } | null;
  dealId: string;
  onSave?: () => void;
}

// Colors matching mobile editor
const COLORS = [
  '#FFFFFF',
  '#14191A',
  '#4275FD',
  '#1DD1A1',
  '#FECA43',
  '#FF6A00',
  '#DB2408',
  '#FA52EE',
  '#8A52FA',
];
function PhotoAnnotator({ open, onClose, photo, onSave }: PhotoAnnotatorProps) {
  const { success: toastSuccess, error: toastError } = useToast();
  const canvasHostRef = React.useRef<HTMLDivElement>(null);
  const fabricCanvasRef = React.useRef<fabric.Canvas | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const calcOffsetRafRef = React.useRef<number | null>(null);

  const [isEditing, setIsEditing] = React.useState(false);
  const [tags, setTags] = React.useState<string[]>([]);
  const [description, setDescription] = React.useState('');
  const [newTag, setNewTag] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [currentColor, setCurrentColor] = React.useState('#FF6A00');
  const [isDrawMode, setIsDrawMode] = React.useState(true);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [historyStack, setHistoryStack] = React.useState<string[]>([]);
  const [editSessionId, setEditSessionId] = React.useState(0);

  // Text editing state
  const [textInputOpen, setTextInputOpen] = React.useState(false);
  const [textInputValue, setTextInputValue] = React.useState('');
  const editingTextRef = React.useRef<fabric.Text | null>(null);

  const scheduleCalcOffset = React.useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) {
      return;
    }

    if (calcOffsetRafRef.current != null) {
      cancelAnimationFrame(calcOffsetRafRef.current);
    }

    calcOffsetRafRef.current = requestAnimationFrame(() => {
      calcOffsetRafRef.current = null;
      canvas.calcOffset();
      canvas.requestRenderAll();
    });
  }, []);

  const applyInteractionMode = React.useCallback(
    (canvas: fabric.Canvas, drawMode: boolean) => {
      canvas.isDrawingMode = drawMode;
      canvas.freeDrawingCursor = 'crosshair';
      canvas.hoverCursor = drawMode ? 'crosshair' : 'move';
      canvas.moveCursor = 'move';
      canvas.defaultCursor = drawMode ? 'crosshair' : 'default';

      if (!drawMode) {
        canvas.selection = true;
        canvas.getObjects().forEach((obj: any) => {
          obj.selectable = true;
          obj.evented = true;
          obj.hasControls = true;
          obj.hasBorders = true;
          obj.lockMovementX = false;
          obj.lockMovementY = false;
          obj.lockScalingX = false;
          obj.lockScalingY = false;
          obj.lockRotation = false;
          obj.lockScalingFlip = false;
          obj.setCoords();
        });
      } else {
        canvas.selection = false;
        canvas.discardActiveObject();
      }

      canvas.requestRenderAll();
    },
    []
  );

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    let cancelled = false;

    if (photo && open) {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      setTags(photo.tags || []);
      setDescription(photo.description || '');
      setHasChanges(false);
      setIsEditing(false);
      setImageLoaded(false);
      setHistoryStack([]);
      setIsDrawMode(true);
      setImageUrl(null);

      const loadImage = async () => {
        try {
          if (photo.type?.startsWith('image/')) {
            const result = await Storage.get(photo.imageName, {
              level: 'public',
              download: true,
            });
            if (cancelled) {
              return;
            }
            const blob = (result as any).Body as Blob;
            const reader = new FileReader();
            reader.onloadend = () => {
              if (cancelled) {
                return;
              }
              setImageUrl(reader.result as string);
            };
            reader.readAsDataURL(blob);
          } else {
            const url = await Storage.get(photo.imageName, { level: 'public' });
            if (cancelled) {
              return;
            }
            setImageUrl(url as string);
          }
        } catch (err) {
          console.error('Error loading image:', err);
          const url = await Storage.get(photo.imageName, { level: 'public' });
          if (cancelled) {
            return;
          }
          setImageUrl(url as string);
        }
      };
      loadImage();
    }

    if (!open) {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      if (canvasHostRef.current) {
        canvasHostRef.current.innerHTML = '';
      }
      setIsEditing(false);
      setIsDrawMode(true);
      setImageLoaded(false);
      setHistoryStack([]);
      setImageUrl(null);
    }
    return () => {
      cancelled = true;
    };
  }, [photo, open]);

  React.useEffect(() => {
    if (!isEditing) {
      return;
    }

    const container = containerRef.current;
    const onScrollOrResize = () => scheduleCalcOffset();

    container?.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, { passive: true });

    return () => {
      container?.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize);

      if (calcOffsetRafRef.current != null) {
        cancelAnimationFrame(calcOffsetRafRef.current);
        calcOffsetRafRef.current = null;
      }
    };
  }, [isEditing, scheduleCalcOffset]);

  // Initialize canvas when entering edit mode
  React.useEffect(() => {
    if (
      !isEditing ||
      !canvasHostRef.current ||
      !imageUrl ||
      !containerRef.current
    ) {
      return;
    }

    const container = containerRef.current;
    const maxW = container.clientWidth - 48;
    const maxH = window.innerHeight - 400;
    const hostEl = canvasHostRef.current;

    const img = new Image();
    img.onload = () => {
      const scale = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;

      hostEl.innerHTML = '';
      const canvasEl = document.createElement('canvas');
      hostEl.appendChild(canvasEl);

      const canvas = new fabric.Canvas(canvasEl, {
        width: w,
        height: h,
        isDrawingMode: true,
        selection: true,
        preserveObjectStacking: true,
      });
      fabricCanvasRef.current = canvas;

      const fabricCanvas = canvas as FabricCanvasWithElements;
      fabricCanvas.upperCanvasEl.style.touchAction = 'none';
      fabricCanvas.upperCanvasEl.style.userSelect = 'none';
      fabricCanvas.upperCanvasEl.style.webkitUserSelect = 'none';
      fabricCanvas.upperCanvasEl.style.pointerEvents = 'auto';
      fabricCanvas.lowerCanvasEl.style.pointerEvents = 'none';
      fabricCanvas.upperCanvasEl.tabIndex = 0;
      fabricCanvas.upperCanvasEl.focus({ preventScroll: true });
      applyInteractionMode(canvas, isDrawMode);

      canvas.freeDrawingBrush.color = currentColor;
      canvas.freeDrawingBrush.width = 4;

      fabric.Image.fromURL(imageUrl, (fabricImg) => {
        fabricImg.scaleToWidth(w);
        canvas.setBackgroundImage(fabricImg, () => {
          canvas.renderAll();
          setImageLoaded(true);
          saveHistory();
          scheduleCalcOffset();
        });
      });

      canvas.on('object:added', () => setHasChanges(true));
      canvas.on('object:modified', () => {
        setHasChanges(true);
        saveHistory();
      });

      canvas.on('mouse:dblclick', (opt) => {
        const target = opt.target;
        if (target && target.type === 'text') {
          editingTextRef.current = target as fabric.Text;
          setTextInputValue((target as fabric.Text).text || '');
          setTextInputOpen(true);
        }
      });

      scheduleCalcOffset();
    };
    img.src = imageUrl;

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      hostEl.innerHTML = '';
    };
  }, [editSessionId, isEditing, imageUrl]);

  const startEditing = React.useCallback(() => {
    setImageLoaded(false);
    setIsDrawMode(true);
    setIsEditing(true);
    setEditSessionId((prev) => prev + 1);
  }, []);

  const stopEditing = React.useCallback(() => {
    setIsEditing(false);
    setImageLoaded(false);
  }, []);

  // Update brush color
  React.useEffect(() => {
    if (fabricCanvasRef.current?.freeDrawingBrush) {
      fabricCanvasRef.current.freeDrawingBrush.color = currentColor;
    }
  }, [currentColor]);

  // Toggle draw mode
  React.useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) {
      return;
    }
    applyInteractionMode(canvas, isDrawMode);
    scheduleCalcOffset();
  }, [applyInteractionMode, isDrawMode, scheduleCalcOffset]);

  const saveHistory = React.useCallback(() => {
    if (fabricCanvasRef.current) {
      setHistoryStack((prev) => [
        ...prev,
        JSON.stringify(fabricCanvasRef.current!.toJSON()),
      ]);
    }
  }, []);

  const handleUndo = React.useCallback(() => {
    if (historyStack.length > 1 && fabricCanvasRef.current) {
      const newStack = historyStack.slice(0, -1);
      setHistoryStack(newStack);
      const canvas = fabricCanvasRef.current;
      canvas.loadFromJSON(newStack[newStack.length - 1], () => {
        canvas.renderAll();
        applyInteractionMode(canvas, isDrawMode);
        scheduleCalcOffset();
      });
    }
  }, [applyInteractionMode, historyStack, isDrawMode, scheduleCalcOffset]);

  const addShape = React.useCallback(
    (type: 'text' | 'rect' | 'circle' | 'line') => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) {
        return;
      }

      canvas.isDrawingMode = false;
      setIsDrawMode(false);

      const shapeDefaults = {
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        lockRotation: false,
        lockScalingX: false,
        lockScalingY: false,
        lockMovementX: false,
        lockMovementY: false,
      };

      let obj: fabric.Object;
      const center = {
        left: canvas.width! / 2 - 50,
        top: canvas.height! / 2 - 50,
      };

      switch (type) {
        case 'text': {
          editingTextRef.current = null;
          setTextInputValue('');
          setTextInputOpen(true);
          return;
        }
        case 'rect':
          obj = new fabric.Rect({
            ...center,
            width: 100,
            height: 80,
            fill: 'transparent',
            stroke: currentColor,
            strokeWidth: 3,
            ...shapeDefaults,
          });
          break;
        case 'circle':
          obj = new fabric.Circle({
            ...center,
            radius: 50,
            fill: 'transparent',
            stroke: currentColor,
            strokeWidth: 3,
            ...shapeDefaults,
          });
          break;
        case 'line':
          obj = new fabric.Line(
            [center.left, center.top, center.left + 100, center.top],
            {
              stroke: currentColor,
              strokeWidth: 3,
              ...shapeDefaults,
            }
          );
          break;
        default:
          return;
      }

      canvas.add(obj);
      canvas.setActiveObject(obj);
      obj.setCoords();
      canvas.requestRenderAll();
      saveHistory();
    },
    [currentColor, saveHistory]
  );

  const handleTextSubmit = React.useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !textInputValue.trim()) {
      setTextInputOpen(false);
      setTextInputValue('');
      editingTextRef.current = null;
      return;
    }

    if (editingTextRef.current) {
      editingTextRef.current.set('text', textInputValue);
      canvas.renderAll();
      setHasChanges(true);
      saveHistory();
    } else {
      const center = {
        left: canvas.width! / 2,
        top: canvas.height! / 2,
      };

      const textObj = new fabric.Text(textInputValue, {
        ...center,
        fontSize: 28,
        fill: currentColor,
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        originX: 'center',
        originY: 'center',
      });

      canvas.add(textObj);
      canvas.setActiveObject(textObj);
      textObj.setCoords();
      canvas.requestRenderAll();
      setHasChanges(true);
      saveHistory();
    }

    setTextInputOpen(false);
    setTextInputValue('');
    editingTextRef.current = null;
  }, [currentColor, saveHistory, textInputValue]);

  const handleTextCancel = React.useCallback(() => {
    setTextInputOpen(false);
    setTextInputValue('');
    editingTextRef.current = null;
  }, []);

  const handleRotate = React.useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) {
      return;
    }

    const bg = canvas.backgroundImage as fabric.Image;
    if (bg) {
      bg.rotate((bg.angle || 0) + 90);
      canvas.renderAll();
      setHasChanges(true);
      saveHistory();
    }
  }, [saveHistory]);

  const handleAddTag = React.useCallback(() => {
    const trimmed = newTag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed]);
      setNewTag('');
      setHasChanges(true);
    }
  }, [newTag, tags]);

  const handleRemoveTag = React.useCallback((tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
    setHasChanges(true);
  }, []);

  const handleSave = React.useCallback(async () => {
    if (!photo?.fileId || !hasChanges) {
      stopEditing();
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      let newUrl = photo.imageName;

      if (isEditing && fabricCanvasRef.current) {
        const dataUrl = fabricCanvasRef.current.toDataURL({
          format: 'png',
          quality: 1,
        });
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const fileName = `edited_${Date.now()}_${photo.imageName
          .split('/')
          .pop()}`;
        await Storage.put(fileName, blob, {
          contentType: 'image/png',
          level: 'public',
        });
        newUrl = fileName;
      }

      await API.graphql(
        graphqlOperation(mutations.updateDealFile, {
          input: {
            id: photo.fileId,
            description,
            tags,
            ...(newUrl !== photo.imageName && { url: newUrl }),
          },
        })
      );

      toastSuccess('Photo updated');
      onSave?.();
      stopEditing();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      toastError('Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [
    photo,
    hasChanges,
    isEditing,
    description,
    tags,
    onSave,
    onClose,
    stopEditing,
    toastSuccess,
    toastError,
  ]);

  const handleClose = React.useCallback(() => {
    if (hasChanges && !window.confirm('Discard changes?')) {
      return;
    }
    stopEditing();
    onClose();
  }, [hasChanges, onClose, stopEditing]);

  const isVideo = photo?.type?.startsWith('video/');
  const isPdf = photo?.type === 'application/pdf';

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          handleClose();
        }
      }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Photo</DialogTitle>
          <DialogDescription className="sr-only">
            Annotate and edit photo with drawing tools, tags, and description
          </DialogDescription>
        </DialogHeader>

        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto space-y-4 py-2"
        >
          {isEditing ? (
            <div className="space-y-3">
              {/* Toolbar */}
              <div className="flex items-center gap-1.5 p-2 bg-muted rounded-lg flex-wrap">
                {/* Color swatches */}
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCurrentColor(c)}
                    className={cn(
                      'size-6 rounded-full cursor-pointer transition-all shrink-0',
                      currentColor === c
                        ? 'ring-2 ring-primary ring-offset-2 ring-offset-muted'
                        : 'border border-border'
                    )}
                    style={{ backgroundColor: c }}
                    aria-label={`Color ${c}`}
                  />
                ))}

                <div className="w-px h-6 bg-border mx-1" />

                {/* Draw/Select toggle */}
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setIsDrawMode(!isDrawMode)}
                        className={cn(
                          'size-8 rounded-md flex items-center justify-center transition-colors',
                          isDrawMode
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background text-foreground hover:bg-accent'
                        )}
                      >
                        {isDrawMode ? (
                          <Paintbrush className="size-4" />
                        ) : (
                          <MousePointer2 className="size-4" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isDrawMode ? 'Drawing Mode' : 'Select Mode'}
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => addShape('text')}
                        className="size-8 rounded-md flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                      >
                        <Type className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Add Text</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => addShape('rect')}
                        className="size-8 rounded-md flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                      >
                        <Square className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Add Rectangle</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => addShape('circle')}
                        className="size-8 rounded-md flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                      >
                        <Circle className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Add Circle</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => addShape('line')}
                        className="size-8 rounded-md flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                      >
                        <Minus className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Add Line</TooltipContent>
                  </Tooltip>

                  <div className="w-px h-6 bg-border mx-1" />

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleRotate}
                        className="size-8 rounded-md flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                      >
                        <RotateCw className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Rotate</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleUndo}
                        disabled={historyStack.length <= 1}
                        className="size-8 rounded-md flex items-center justify-center text-foreground hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Undo2 className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Undo</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Canvas area */}
              <div className="flex items-center justify-center bg-muted rounded-lg p-4 min-h-[200px]">
                {!imageLoaded && (
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                )}
                <div
                  ref={canvasHostRef}
                  className={cn(!imageLoaded && 'hidden')}
                />
              </div>

              {/* Text Input Dialog */}
              <Dialog
                open={textInputOpen}
                onOpenChange={(val) => {
                  if (!val) {
                    handleTextCancel();
                  }
                }}
              >
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>
                      {editingTextRef.current ? 'Edit Text' : 'Add Text'}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                      Enter text to add to the photo annotation
                    </DialogDescription>
                  </DialogHeader>
                  <textarea
                    autoFocus
                    value={textInputValue}
                    onChange={(e) => setTextInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleTextSubmit();
                      } else if (e.key === 'Escape') {
                        handleTextCancel();
                      }
                    }}
                    placeholder="Enter your text..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleTextCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleTextSubmit}
                      disabled={!textInputValue.trim()}
                    >
                      {editingTextRef.current ? 'Update' : 'Add'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Image preview */}
              <div className="relative flex items-center justify-center">
                {imageUrl ? (
                  <>
                    {isVideo ? (
                      <video
                        src={imageUrl}
                        controls
                        className="max-w-full max-h-[400px] rounded-lg"
                      />
                    ) : isPdf ? (
                      <div className="w-full h-[400px] flex items-center justify-center bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">
                          PDF Preview
                        </p>
                      </div>
                    ) : (
                      <img
                        src={imageUrl}
                        alt="Photo"
                        className="max-w-full max-h-[400px] rounded-lg object-contain"
                      />
                    )}
                    {!isVideo && !isPdf && (
                      <Button
                        size="sm"
                        onClick={startEditing}
                        className="absolute bottom-3 right-3 gap-1.5"
                      >
                        <Paintbrush className="size-3.5" />
                        Edit Image
                      </Button>
                    )}
                  </>
                ) : (
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                )}
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Tags
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="size-4 rounded-full flex items-center justify-center hover:bg-destructive/20 transition-colors"
                      >
                        <X className="size-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add tag..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    className="flex-1 px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                    className="shrink-0"
                  >
                    <Plus className="size-4" />
                  </Button>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setHasChanges(true);
                  }}
                  placeholder="Add description..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-border pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="gap-1.5"
          >
            {isSaving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default React.memo(PhotoAnnotator);
